import React from 'react';
import I18n from 'i18n-js';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import FormGroupWithRadio from '@reverbdotcom/commons/src/components/form_group_with_radio';
import SiteModule from '@reverbdotcom/commons/src/components/site_module';
import experiments from '@reverbdotcom/commons/src/experiments';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import { I18N } from '@reverbdotcom/commons/src/components/translate';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { RCAlertBox } from '@reverbdotcom/commons/src/rc/RCAlertBox';
import { RCTag } from '@reverbdotcom/commons/src/rc/RCTag';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { useExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';

import { core_apimessages_ShippingLabelPackage } from '../../schemas/rql';
import CustomPackageInputs from '../discovery/shipping_labels/custom_package_inputs';
import { MeasurementSystem, buildNewPackage } from '../discovery/shipping_labels/shipping_label_package';
import URLHelpers from '../shared/url_helpers';

const USPS_DIVISOR = 166;
const USPS_CUBIC_VOLUME_LOWER_LIMIT_INCHES = 1728; // the USPS-defined threshold where dimensional weight consideration begins
const CARRIER_CALC_MAX_CUBIC_VOLUME_INCHES = 1660; // the reverb-defined limit for carrier-calc eligiblity (dimensional weight of 10lb)
const CARRIER_CALC_MAX_LENGTH_INCHES = 22;
const CARRIER_CALC_MAX_WEIGHT_POUNDS = 10;

function PackageSizeWarning({ shippingPackage, setCarrierCalculatedMode }) {
  const { length, width, height, weight } = shippingPackage;
  if (!length || !width || !height || !weight) { return null; }
  const allInches = [length, width, height].every((dim) => dim.unit === 'in');
  const weightIsInPounds = !!weight && weight.unit === 'lb';
  if (!allInches || !weightIsInPounds) { return null; }

  const lengthInches = Number(length?.value);
  const widthInches = Number(width?.value);
  const heightInches = Number(height?.value);
  const weightPounds = Number(weight?.value);

  const allDimensionsEntered = [lengthInches, widthInches, heightInches, weightPounds].every((v) => v > 0);

  if (!allDimensionsEntered) { return null; }

  const volume_cubic_inches = lengthInches * widthInches * heightInches;

  let dimensionalWeightIfApplicable;
  if (volume_cubic_inches > USPS_CUBIC_VOLUME_LOWER_LIMIT_INCHES) {
    dimensionalWeightIfApplicable = Math.ceil(volume_cubic_inches / USPS_DIVISOR);
  }

  const exceedsActualWeightLimit = weightPounds > CARRIER_CALC_MAX_WEIGHT_POUNDS;
  const exceedsLengthLimit = [lengthInches, widthInches, heightInches].some((v) => v > CARRIER_CALC_MAX_LENGTH_INCHES);
  const exceedsDimensionalWeightLimit = !!dimensionalWeightIfApplicable && dimensionalWeightIfApplicable > CARRIER_CALC_MAX_WEIGHT_POUNDS;

  let errorTranslationKey;
  if (exceedsActualWeightLimit) {
    errorTranslationKey = 'exceedsActualWeightLimit';
  } else if (exceedsLengthLimit) {
    errorTranslationKey = 'exceedsLengthLimit';
  } else if (exceedsDimensionalWeightLimit) {
    errorTranslationKey = 'exceedsDimensionalWeightLimit';
  }

  if (errorTranslationKey) {
    return (
      <RCAlertBox data-package-warning type="error">
        <I18N
          html
          text={`discovery.dashboard.selling.shippingPackageType.carrierCalculated.exceedsMaxWarnings.${errorTranslationKey}`}
          args={{
            maxLengthInches: String(CARRIER_CALC_MAX_LENGTH_INCHES),
            maxWeightPounds: String(CARRIER_CALC_MAX_WEIGHT_POUNDS),
            maxCubicInches: String(CARRIER_CALC_MAX_CUBIC_VOLUME_INCHES),
          }}
        >
          <button
            className="button-as-link"
            type="button"
            onClick={() => setCarrierCalculatedMode(false)}
          >
            {I18n.t('discovery.dashboard.selling.shippingPackageType.carrierCalculated.exceedsMaxWarnings.setCustomRate')}
          </button>
        </I18N>
      </RCAlertBox>
    );
  }

  return null;
}

function CarrierCalculatedDescription() {
  return (
    <ul className="sell-form-package-inputs-container__list-group">
      <li className="sell-form-package-inputs-container__list-item">
        {I18n.t('discovery.dashboard.selling.shippingPackageType.carrierCalculated.description.dimensionLimits', {
          maxLengthInches: String(CARRIER_CALC_MAX_LENGTH_INCHES),
          maxWeightPounds: String(CARRIER_CALC_MAX_WEIGHT_POUNDS),
        })}
      </li>

      <li className="sell-form-package-inputs-container__list-item">
        {I18n.t('discovery.dashboard.selling.shippingPackageType.carrierCalculated.description.willSendLabel')}
      </li>

      <li className="sell-form-package-inputs-container__list-item">
        <I18N
          text="discovery.dashboard.selling.shippingPackageType.carrierCalculated.description.bySelecting"
        >
          <CoreLink to={URLHelpers.legalPagePath('calculated-shipping-terms-and-conditions')} target="_blank">
            {I18n.t('discovery.dashboard.selling.shippingPackageType.carrierCalculated.description.terms')}
          </CoreLink>
        </I18N>
      </li>
    </ul>
  );
}

function RadioLabel({ shippingType }: { shippingType: 'carrierCalculated' | 'custom' }) {
  const forCarrierCalculated = shippingType === 'carrierCalculated';
  return (
    <label htmlFor={`${shippingType}ShippingRadio`}>
      <span className="mr-1 size-120 weight-normal">
        {I18n.t(`discovery.dashboard.selling.shippingPackageType.${shippingType}.title`)}

        {forCarrierCalculated && (
          <span className="sell-form-package-inputs-container__title-emphasize">
            {I18n.t(`discovery.dashboard.selling.shippingPackageType.${shippingType}.carrierSpecification`)}
          </span>
        )}
      </span>
      <RCTag variant="highlight">
        {I18n.t(`discovery.dashboard.selling.shippingPackageType.${shippingType}.badgeText`)}
      </RCTag>
    </label>
  );
}

function RadioHelpText({ shippingType, shopAddressSummary }: { shippingType: 'carrierCalculated' | 'custom'; shopAddressSummary?: string; }) {
  const forCarrierCalculated = shippingType === 'carrierCalculated';

  return (
    <div>
      <p className="sell-form-package-inputs-container__help-text">
        {forCarrierCalculated && (
          <I18N
            text="discovery.dashboard.selling.shippingPackageType.carrierCalculated.helpText"
            args={{ shopAddressSummary }}
          >
            <CoreLink to={URLHelpers.shopEditPath({ anchor: 'edit_shop_location' })} target="_blank">
              {I18n.t('discovery.dashboard.selling.shippingPackageType.carrierCalculated.shopAddress')}
            </CoreLink>
          </I18N>
        )}

        {!forCarrierCalculated && I18n.t('discovery.dashboard.selling.shippingPackageType.custom.helpText')}
      </p>
    </div>
  );
}

interface IProps {
  shippingPackage?: core_apimessages_ShippingLabelPackage;
  standardAmountUsd?: string;
  combineAndSaveAmountUsd?: string;
  forNewListing?: boolean;
  shopAddressSummary: string;
}

// This component will only be rendered in the sell form for USD/US_CON sellers, who are eligible for carrier-calculated shipping. It
// allows experiment users to submit package information, but otherwise replicates the default shipping price inputs on the current sell form.
export default function SellFormPackageInputsContainer(props: IProps) {
  const user = useUser();
  const isInCarrierCalcExperiment = useExpEnabled(experiments.CARRIER_CALCULATED_SELLER);

  const [inCarrierCalculatedMode, setCarrierCalculatedMode] = React.useState(isInCarrierCalcExperiment && !!props.shippingPackage);
  const [shippingPackage, setShippingPackage] = React.useState(props.shippingPackage || buildNewPackage(MeasurementSystem.IMPERIAL));
  const [standardAmountUsd, setStandardAmountUsd] = React.useState(props.standardAmountUsd || '');
  const [combineAndSaveAmountUsd, setCombineAndSaveAmountUsd] = React.useState(props.combineAndSaveAmountUsd || '');

  useViewTracking({
    componentName: 'SellFormPackageInputsContainer',
    eventName: MParticleEventName.ComponentView,
    forNewListing: props.forNewListing,
    experiments: formatExperimentsForEventAttributes(user, [experiments.CARRIER_CALCULATED_SELLER]),
  }, true);

  return (
    <>
      {isInCarrierCalcExperiment && (
        <div className="scaling-mb-2">
          <p><strong>{I18n.t('discovery.dashboard.selling.shippingPackageType.header')}</strong></p>
          <SiteModule>
            <FormGroupWithRadio
              inputName="customShippingRadio"
              label={<RadioLabel shippingType="custom" />}
              helpText={<RadioHelpText shippingType="custom" />}
              value={`${!inCarrierCalculatedMode}`}
              checked={!inCarrierCalculatedMode}
              updateField={() => setCarrierCalculatedMode(false)}
            />
          </SiteModule>
          <SiteModule footerContent={<CarrierCalculatedDescription />}>
            <FormGroupWithRadio
              inputName="carrierCalculatedShippingRadio"
              label={<RadioLabel shippingType="carrierCalculated" />}
              helpText={<RadioHelpText shippingType="carrierCalculated" shopAddressSummary={props.shopAddressSummary} />}
              value={`${inCarrierCalculatedMode}`}
              checked={inCarrierCalculatedMode}
              updateField={() => setCarrierCalculatedMode(true)}
            />
          </SiteModule>
        </div>
      )}

      <input
        type="hidden"
        id="product_shipping_rates__region_type"
        name="product[shipping][rates][][region_type]"
        value="subregion"
        data-region-type="true"
      />
      <input
        type="hidden"
        id="product_shipping_rates__region_code"
        name="product[shipping][rates][][region_code]"
        value="US_CON"
        data-region-code="true"
      />

      {inCarrierCalculatedMode && (
        <>
          <CustomPackageInputs
            shippingPackage={shippingPackage}
            measurementSystem={MeasurementSystem.IMPERIAL}
            updatePackageDimensions={setShippingPackage}
            forCarrierCalculated
            hideMeasurementSystemControls
          />
          <input
            id="product_shipping_US_CON_rate_type"
            type="hidden"
            name="product[shipping][rates][][rate_type]"
            value="carrier_calculated"
          />

          <PackageSizeWarning shippingPackage={shippingPackage} setCarrierCalculatedMode={setCarrierCalculatedMode} />
        </>
      )}

      {!inCarrierCalculatedMode && (
        <div className="shipping-rate-card__rates">
          {/* The `Standard Price` and `Combine & Save` form groups match form groups used in core */}
          {/* These form groups will move to react in a future iteration of the sell form */}
          {/* eslint-disable */}
          <div className="shipping-rate-card__manual__price__entry">
            <div className="shipping-rate-card__manual__price__entry__input">
              {/* Standad price form group */}
              <div className="form-group">
                <div className="form-group__header">
                  <label htmlFor="product_subregion_US_CON_standard">
                    {I18n.t('discovery.dashboard.selling.shippingRates.rateFields.rate')}
                  </label>
                </div>
                <div className="form-group__fields">
                  <div className="input-group">
                    <span className="input-group__text">
                      <span className="fa fa-usd"></span>
                    </span>
                    <input
                      id="product_subregion_US_CON_standard"
                      name="product[shipping][rates][][rate]"
                      type="text"
                      className="input-group__input required"
                      data-shipping-rate="true"
                      data-shipping-country-code="US_CON"
                      data-required-shipping-rate="true"
                      pattern="^[\d.,\s]+$"
                      required
                      value={standardAmountUsd}
                      onChange={event => setStandardAmountUsd(event.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Combine & Save price form group */}
              <div className="form-group">
                <div
                  className="form-group__header"
                  data-tooltip-text={`${I18n.t('discovery.dashboard.selling.shippingRates.rateFields.combinedShippingTooltip')} ${I18n.t('discovery.dashboard.selling.shippingRates.rateFields.validationErrors.greaterThanStandard')}`}
                >
                  <label className="icon-r-question-circle" htmlFor="product_subregion_US_CON_combined">
                    {I18n.t('discovery.dashboard.selling.shippingRates.rateFields.incrementalRate')}
                  </label>
                  <div className="span label-tag ml-1">
                    {I18n.t('discovery.dashboard.selling.shippingRates.rateFields.optional')}
                  </div>
                </div>

                <div className="form-group__fields">
                  <div className="input-group">
                    <span className="input-group__text">
                      <span className="fa fa-usd"></span>
                    </span>
                    <input
                      type="text"
                      name="product[shipping][rates][][incremental_rate]"
                      id="product_subregion_US_CON_combined"
                      className="input-group__input"
                      data-shipping-rate="true"
                      data-shipping-country-code="US_CON"
                      pattern="^[\d.,\s]+$"
                      value={combineAndSaveAmountUsd}
                      onChange={event => setCombineAndSaveAmountUsd(event.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* eslint-enable */}
        </div>
      )}
    </>
  );
}
